import React, { ReactNode, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { every, startsWith, filter, map, includes, isEqual } from "lodash";
import Modal from "components/shared/Modal";
import useFileUpload from "../../../hooks/useFileUpload";
import {
  getComposerImageAttachments,
  getComposerFileAttachments,
} from "selectors/chat/composer";
import { FileData } from "../../../@types/files";

export default function DragDropWrapper({
  chatId,
  children,
  isModalOpen,
}: {
  chatId: string;
  children: ReactNode;
  isModalOpen?: boolean;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [fileType, setFileType] = useState<"files" | "images">("files");
  const [localFiles, setLocalFiles] = useState<(File & FileData)[]>([]);
  const [filesToUpload, setFilesToUpload] = useState<(File & FileData)[]>([]);

  const handleUploadFiles = useFileUpload({
    setSelectedFiles: setLocalFiles,
    selectedFiles: localFiles,
  });
  const selectedImages = useSelector((state) =>
    getComposerImageAttachments(state, { chatId }),
  );
  const selectedFiles = useSelector((state) =>
    getComposerFileAttachments(state, { chatId }),
  );

  // Set local files if selected files are updated by upload modal
  useEffect(() => {
    const changedFiles = [...(selectedImages || []), ...(selectedFiles || [])];
    if (!isEqual(localFiles, changedFiles)) {
      setLocalFiles(changedFiles);
    }
  }, [selectedImages, selectedFiles]);

  const localSelectedFiles = selectedFiles
    ? filter(localFiles, (file) => includes(map(selectedFiles, "id"), file.id))
    : [];
  const localSelectedImages = selectedImages
    ? filter(localFiles, (file) => includes(map(selectedImages, "id"), file.id))
    : [];
  const maxFileAttachments =
    Preload.current_network.config.chat.message.max_file_attachments;
  const maxImageAttachments =
    Preload.current_network.config.chat.message.max_image_attachments;

  function handleDragEnter(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (isModalOpen) return;

    setIsDragging(true);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    if (isModalOpen) return;

    setIsDragging(true);
  }

  function handleDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    if (isModalOpen) return;

    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();

    if (isModalOpen) return;

    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);

    if (files.length) {
      const areAllImages = every(files, (file) =>
        startsWith(file.type, "image/"),
      );

      if (areAllImages) {
        setShowModal(true);
        setFilesToUpload(files);
      } else {
        if (localSelectedFiles.length < maxFileAttachments) {
          handleFileUpload(files, "files");
        } else
          toastr.error(
            I18n.t("js.chat.composer.max_files_reached", {
              max_files: maxFileAttachments,
            }),
          );
      }
    }
  }

  function handleDecision(type: "images" | "files") {
    setShowModal(false);

    const maxFiles =
      type === "images" ? maxImageAttachments : maxFileAttachments;
    const localSelected =
      type === "images" ? localSelectedImages : localSelectedFiles;

    if (localSelected.length < maxFiles) handleFileUpload(filesToUpload, type);
    else
      toastr.error(
        I18n.t(`js.chat.composer.max_${type}_reached`, {
          max_files: maxFiles,
        }),
      );
  }

  function handleFileUpload(
    files: (File & FileData)[],
    type: "images" | "files",
  ) {
    const otherLocalSelected =
      type === "images" ? localSelectedFiles : localSelectedImages;

    const maxFiles =
      type === "images" ? maxImageAttachments : maxFileAttachments;

    handleUploadFiles(files, type, maxFiles + otherLocalSelected.length);
    setFileType(type);
  }

  const dispatch = useDispatch();

  useEffect(() => {
    const newFiles =
      fileType === "images"
        ? filter(
            localFiles,
            (file) => !includes(map(selectedFiles, "id"), file.id),
          )
        : filter(
            localFiles,
            (file) => !includes(map(selectedImages, "id"), file.id),
          );

    dispatch({
      type:
        fileType === "images"
          ? "chat/COMPOSER/ADD_OR_UPDATE_IMAGE_ATTACHMENTS"
          : "chat/COMPOSER/ADD_OR_UPDATE_FILE_ATTACHMENTS",
      chatId,
      fileAttachments: newFiles,
    });
  }, [localFiles]);

  return (
    <>
      {showModal && (
        <Modal
          title={I18n.t(
            "js.chat.composer.file_upload.dropzone.decision_modal.title",
          )}
          onClose={() => setShowModal(false)}
          useMinHeightMax
          static
        >
          <div className="btn-group flex justify-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleDecision("images")}
            >
              {I18n.t(
                "js.chat.composer.file_upload.dropzone.decision_modal.add_images",
              )}
            </button>
            <button
              type="button"
              className="btn btn-light"
              onClick={() => handleDecision("files")}
            >
              {I18n.t(
                "js.chat.composer.file_upload.dropzone.decision_modal.add_files",
              )}
            </button>
          </div>
        </Modal>
      )}
      <div
        className="stack-container"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div
          className={classNames("chat__container stack-item", {
            blur: isDragging && !isModalOpen,
          })}
        >
          {children}
        </div>
        {isDragging && !isModalOpen && (
          <div className="stack-item flex h-full w-full z-50 items-center justify-center text-2xl">
            <span className="text-muted font-bold p-10">
              {I18n.t(`js.files.uploader.drag_and_drop_files`)}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
